import dayjs, {Dayjs} from "dayjs";
import {FulfillmentType, OpeningHours} from "@/data/entities/restaurant";

export const ASAP = "ASAP";

const SLOT_MINUTES = 15;
// The earliest slot is at least this far from now
const LEAD_TIME_MINUTES = 15;

/** "18:30:00" or "18:30" on the given day. */
const atTime = (day: Dayjs, time: string): Dayjs => {
    const [hour, minute, second] = time.split(":").map(Number);
    return day.hour(hour).minute(minute).second(second || 0).millisecond(0);
};

/** Rounds up to the next whole slot, e.g. 18:07 -> 18:15. */
const roundUpToSlot = (time: Dayjs): Dayjs => {
    const start = time.second(0).millisecond(0);
    const remainder = start.minute() % SLOT_MINUTES;
    const isOnSlot = remainder === 0 && time.second() === 0 && time.millisecond() === 0;
    return isOnSlot ? start : start.add(SLOT_MINUTES - remainder, "minute");
};

/**
 * Times that can still be requested today, every 15 minutes within the
 * delivery or restaurant opening hours (matching the backend check, which
 * only accepts a time on the current day between open and close, inclusive).
 */
export const requestedTimeSlots = (
    openingWeek: OpeningHours[],
    fulfillmentType: FulfillmentType,
    now: Dayjs = dayjs(),
): Dayjs[] => {
    const today = openingWeek.find(day => day.date === now.format("YYYY-MM-DD"));
    if (!today || today.isClosed)
        return [];

    const [openTime, closeTime] = fulfillmentType === "DELIVERY"
        ? [today.deliveryOpen, today.deliveryClose]
        : [today.restaurantOpen, today.restaurantClose];
    if (!openTime || !closeTime)
        return [];

    const open = atTime(now, openTime);
    const close = atTime(now, closeTime);
    const earliest = now.add(LEAD_TIME_MINUTES, "minute");

    const slots: Dayjs[] = [];
    for (let slot = roundUpToSlot(earliest.isAfter(open) ? earliest : open);
         !slot.isAfter(close);
         slot = slot.add(SLOT_MINUTES, "minute")) {
        slots.push(slot);
    }
    return slots;
};

/**
 * Value for the backend: `ASAP` or an ISO-8601 datetime with offset, e.g.
 * `2026-10-15T18:30:00+02:00`.
 */
export const toRequestedTime = (slot: Dayjs): string => slot.format("YYYY-MM-DDTHH:mm:ssZ");